/**
 * Plant Production — Materials, Mix Designs (recipes), and the Production log.
 * New for the RMC conversion. Backend: routers/production.py.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, Beaker, Boxes, LayoutDashboard,
  AlertTriangle, Trash2,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost } from '../lib/api';
import { inrCompact, formatISTDateTime } from '../lib/format';

const TABS = ['Dashboard', 'Materials', 'Mix Designs', 'Production Log'];
const CATEGORIES = ['Cement', 'Sand', 'Aggregate', 'Admixture', 'Fly Ash', 'Water', 'Other'];

export default function Production() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Dashboard');
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.includes(t) && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTab = (t) => { setTabState(t); setSearchParams(t === 'Dashboard' ? {} : { tab: t }); };
  return (
    <div data-testid="production-page">
      <PageHeader
        overline="Plant Production"
        title="Recipe & Production"
        subtitle="Mix designs, raw material stock, and the batch production log."
      />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={'relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}
              data-testid={`prod-tab-${t.toLowerCase().replace(/\s+/g, '-')}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#1F7A6E]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Dashboard' && <DashboardTab />}
          {tab === 'Materials' && <MaterialsTab />}
          {tab === 'Mix Designs' && <MixDesignsTab />}
          {tab === 'Production Log' && <ProductionLogTab />}
        </div>
      </div>
    </div>
  );
}

function KTile({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="kpi-overline">{label}</div>
        {Icon && <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}><Icon size={13} /></div>}
      </div>
      <div className="font-display text-xl font-extrabold tracking-tight">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────────────
function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => { apiGet('/production/dashboard').then(setData).catch(() => toast.error('Failed to load dashboard')); }, []);
  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KTile label="Produced Today" value={`${data.today_qty_m3} m³`} icon={LayoutDashboard} accent="text-[#1F7A6E] bg-teal-50" />
        <KTile label="Mix Designs" value={data.mix_design_count} icon={Beaker} />
        <KTile label="Low Stock Materials" value={data.low_stock_materials.length}
          icon={AlertTriangle} accent={data.low_stock_materials.length ? 'text-red-700 bg-red-50' : 'text-emerald-700 bg-emerald-50'} />
        <KTile label="Grades Produced Today" value={data.today_by_grade.length} icon={Boxes} />
      </div>
      {data.today_by_grade.length > 0 && (
        <div>
          <div className="kpi-overline mb-2">Today's Output by Grade</div>
          <div className="flex flex-wrap gap-2">
            {data.today_by_grade.map((g) => (
              <div key={g.grade} className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm">
                <span className="font-display font-bold">{g.grade}</span> — {g.qty_m3} m³
              </div>
            ))}
          </div>
        </div>
      )}
      {data.low_stock_materials.length > 0 && (
        <div>
          <div className="kpi-overline mb-2 text-red-600">Below Reorder Level</div>
          <div className="bg-red-50 border border-red-200 rounded-sm divide-y divide-red-100">
            {data.low_stock_materials.map((m) => (
              <div key={m.material_id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <span className="font-semibold">{m.name}</span>
                <span className="text-red-700 font-display font-bold">{m.stock} on hand · reorder at {m.reorder_level}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Materials ────────────────────────────────────────────────────────────────
function MaterialsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [receiveFor, setReceiveFor] = useState(null);

  const load = () => {
    setLoading(true);
    apiGet('/production/materials').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load materials')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Raw Materials · {items.length}</div>
        <button onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="material-add-btn">
          <Plus size={13} /> Add Material
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Name</th><th>Category</th><th>UOM</th>
            <th className="text-right">Current Stock</th><th className="text-right">Reorder Level</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">No materials yet — add your first one above.</td></tr>}
            {items.map((m) => (
              <tr key={m.id} className={m.below_reorder ? 'bg-red-50/40' : ''}>
                <td className="font-semibold">{m.name}</td>
                <td>{m.category}</td>
                <td>{m.uom}</td>
                <td className={`text-right tabular-nums font-display font-bold ${m.below_reorder ? 'text-red-600' : ''}`}>{m.current_stock}</td>
                <td className="text-right tabular-nums text-slate-500">{m.reorder_level}</td>
                <td className="text-right">
                  <button onClick={() => setReceiveFor(m)}
                    className="px-3 py-1 text-xs font-semibold text-[#1F7A6E] border border-[#1F7A6E]/40 rounded-sm hover:bg-teal-50"
                    data-testid={`material-receive-${m.id}`}>
                    Receive Stock
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddMaterialModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {receiveFor && <ReceiveStockModal material={receiveFor} onClose={() => setReceiveFor(null)} onSaved={() => { setReceiveFor(null); load(); }} />}
    </div>
  );
}

function ModalShell({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-sm w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function AddMaterialModal({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [uom, setUom] = useState('kg');
  const [reorderLevel, setReorderLevel] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await apiPost('/production/materials', { name: name.trim(), category, uom, reorder_level: Number(reorderLevel || 0) });
      toast.success(`Added "${name}"`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add material'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="Add Raw Material" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="e.g. OPC 53 Cement" autoFocus /></Field>
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="input">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="UOM"><input value={uom} onChange={(e) => setUom(e.target.value)} className="input" placeholder="kg / L" /></Field>
          <Field label="Reorder Level"><input type="number" value={reorderLevel} onChange={(e) => setReorderLevel(e.target.value)} className="input" placeholder="0" /></Field>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Save Material'}
        </button>
      </div>
    </ModalShell>
  );
}

function ReceiveStockModal({ material, onClose, onSaved }) {
  const [qty, setQty] = useState('');
  const [rate, setRate] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!qty || Number(qty) <= 0) return toast.error('Enter a valid quantity');
    if (!rate || Number(rate) < 0) return toast.error('Enter a valid rate');
    setSaving(true);
    try {
      await apiPost(`/production/materials/${material.id}/receive`, { qty: Number(qty), rate: Number(rate), source_ref: sourceRef || null });
      toast.success(`Received ${qty} ${material.uom} of ${material.name}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to receive stock'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Receive Stock — ${material.name}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="text-xs text-slate-500">Current stock: <span className="font-display font-bold text-slate-700">{material.current_stock} {material.uom}</span></div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Quantity (${material.uom})`}><input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="input" autoFocus /></Field>
          <Field label={`Rate (₹/${material.uom})`}><input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="input" /></Field>
        </div>
        <Field label="Source Reference (optional)"><input value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} className="input" placeholder="PO number, vendor challan…" /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Add Stock'}
        </button>
      </div>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}

// ── Mix Designs ──────────────────────────────────────────────────────────────
function MixDesignsTab() {
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([apiGet('/production/mix-designs'), apiGet('/production/materials')])
      .then(([mx, mt]) => { setItems(mx.items || []); setMaterials(mt.items || []); })
      .catch(() => toast.error('Failed to load mix designs'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Mix Designs (Recipes) · {items.length}</div>
        <button onClick={() => setShowAdd(true)}
          disabled={materials.length === 0}
          title={materials.length === 0 ? 'Add at least one material first' : ''}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] disabled:opacity-40 text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="mix-design-add-btn">
          <Plus size={13} /> New Mix Design
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && <div className="text-center py-8 text-slate-400 col-span-2">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-center py-8 text-slate-400 col-span-2">No mix designs yet.</div>}
        {items.map((mx) => (
          <div key={mx.id} className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display font-extrabold text-lg text-[#1F7A6E]">{mx.grade}</div>
                <div className="text-xs text-slate-500">{mx.name}</div>
              </div>
              <Beaker size={18} className="text-slate-300" />
            </div>
            <div className="divide-y divide-slate-100 border-t border-slate-100">
              {mx.materials.map((line, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 text-xs">
                  <span className="text-slate-600">{line.material_name}</span>
                  <span className="font-display font-bold">{line.qty_per_m3} {line.uom} / m³</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showAdd && <AddMixDesignModal materials={materials} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddMixDesignModal({ materials, onClose, onSaved }) {
  const [grade, setGrade] = useState('');
  const [name, setName] = useState('');
  const [lines, setLines] = useState([{ material_id: '', qty_per_m3: '' }]);
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines([...lines, { material_id: '', qty_per_m3: '' }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i, field, value) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));

  const save = async () => {
    if (!grade.trim()) return toast.error('Grade is required (e.g. M25)');
    const validLines = lines.filter((l) => l.material_id && Number(l.qty_per_m3) > 0);
    if (validLines.length === 0) return toast.error('Add at least one material line');
    setSaving(true);
    try {
      const payload = {
        grade: grade.trim(), name: name.trim() || undefined,
        materials: validLines.map((l) => {
          const m = materials.find((mat) => mat.id === l.material_id);
          return { material_id: l.material_id, material_name: m?.name || '', qty_per_m3: Number(l.qty_per_m3), uom: m?.uom || 'kg' };
        }),
      };
      await apiPost('/production/mix-designs', payload);
      toast.success(`Mix design ${grade} saved`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save mix design'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="New Mix Design" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Grade"><input value={grade} onChange={(e) => setGrade(e.target.value)} className="input" placeholder="M25" autoFocus /></Field>
          <Field label="Name (optional)"><input value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="M25 Standard Mix" /></Field>
        </div>
        <div>
          <label className="label">Materials per m³</label>
          <div className="space-y-2">
            {lines.map((line, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={line.material_id} onChange={(e) => updateLine(i, 'material_id', e.target.value)} className="input flex-1">
                  <option value="">Select material…</option>
                  {materials.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.uom})</option>)}
                </select>
                <input type="number" value={line.qty_per_m3} onChange={(e) => updateLine(i, 'qty_per_m3', e.target.value)}
                  className="input w-28" placeholder="Qty" />
                <button onClick={() => removeLine(i)} disabled={lines.length === 1} className="p-2 text-slate-400 hover:text-red-600 disabled:opacity-30"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <button onClick={addLine} className="mt-2 text-xs font-semibold text-[#1F7A6E] flex items-center gap-1"><Plus size={12} /> Add material line</button>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Save Mix Design'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Production Log ───────────────────────────────────────────────────────────
function ProductionLogTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiGet('/production/entries').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load production log')).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full dense-table">
        <thead><tr>
          <th>Entry No</th><th>Date</th><th>Grade</th><th className="text-right">Qty (m³)</th>
          <th>Batch</th><th>Vehicle</th><th className="text-right">Cost / m³</th><th className="text-right">Total Cost</th><th>Origin</th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan="9" className="text-center py-8 text-slate-400">Loading…</td></tr>}
          {!loading && items.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-slate-400">No production entries yet — these are created automatically when you raise a Dispatch Challan.</td></tr>}
          {items.map((e) => (
            <tr key={e.id}>
              <td className="font-mono text-xs">{e.entry_no}</td>
              <td className="text-xs">{formatISTDateTime(e.date)}</td>
              <td className="font-display font-bold">{e.grade}</td>
              <td className="text-right tabular-nums">{e.qty_m3}</td>
              <td className="text-xs">{e.batch_no || '—'}</td>
              <td className="text-xs font-mono">{e.vehicle_no || '—'}</td>
              <td className="text-right tabular-nums">{inrCompact(e.cost_per_m3)}</td>
              <td className="text-right tabular-nums font-display font-bold">{inrCompact(e.total_material_cost)}</td>
              <td>{e.challan_id ? <span className="tag bg-teal-50 text-[#1F7A6E]">Challan</span> : <span className="tag bg-slate-100 text-slate-600">Direct</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
